import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonIcon,
  IonInput,
  IonButton,
  IonSpinner,
  IonContent,
} from '@ionic/react';
import { lockClosedOutline, eyeOutline, eyeOffOutline, checkmarkCircleOutline, alertCircleOutline } from 'ionicons/icons';
import { PageHeader, AppBackground } from '../../components/PageHeader';
import { API_BASE_URL } from '../../config';

const CambiarContrasena: React.FC = () => {
  const history = useHistory();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Password visibility states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError('La nueva contraseña y la confirmación no coinciden.');
      return;
    }

    if (newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Error al actualizar la contraseña');
        return;
      }

      setSuccess('Tu contraseña ha sido actualizada con éxito.');
      // Limpiar campos
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Redirigir al dashboard después de un momento
      setTimeout(() => {
        history.push('/panel');
      }, 2000);

    } catch (err) {
      setError('No se pudo conectar con el servidor. Verifica tu conexión.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage className="app-page">
      <AppBackground />

      <PageHeader title="Cambiar Contraseña" subtitle="Actualiza tus credenciales de acceso" backHref="/panel" disabled={loading} />

      <IonContent className="content-transparent ion-padding-horizontal">
        <div className="app-container">
          <div className="app-card">
            <h3 className="app-card-title">Actualizar Contraseña</h3>
            <p className="app-card-subtitle">Completa los campos para establecer una nueva contraseña de seguridad.</p>

            <form onSubmit={handleSubmit} className="app-form">
              {/* Contraseña Actual */}
              <div className="input-group">
                <span className="input-icon">
                  <IonIcon icon={lockClosedOutline} />
                </span>
                <IonInput
                  type={showCurrent ? 'text' : 'password'}
                  placeholder="Contraseña actual"
                  value={currentPassword}
                  onIonInput={(e: any) => setCurrentPassword(e.detail.value ?? '')}
                  className="app-input"
                  required
                />
                <span
                  className="input-icon-right"
                  onClick={() => setShowCurrent(!showCurrent)}
                >
                  <IonIcon icon={showCurrent ? eyeOffOutline : eyeOutline} />
                </span>
              </div>

              {/* Nueva Contraseña */}
              <div className="input-group">
                <span className="input-icon">
                  <IonIcon icon={lockClosedOutline} />
                </span>
                <IonInput
                  type={showNew ? 'text' : 'password'}
                  placeholder="Nueva contraseña"
                  value={newPassword}
                  onIonInput={(e: any) => setNewPassword(e.detail.value ?? '')}
                  className="app-input"
                  required
                />
                <span
                  className="input-icon-right"
                  onClick={() => setShowNew(!showNew)}
                >
                  <IonIcon icon={showNew ? eyeOffOutline : eyeOutline} />
                </span>
              </div>

              {/* Confirmar Nueva Contraseña */}
              <div className="input-group">
                <span className="input-icon">
                  <IonIcon icon={lockClosedOutline} />
                </span>
                <IonInput
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirmar nueva contraseña"
                  value={confirmPassword}
                  onIonInput={(e: any) => setConfirmPassword(e.detail.value ?? '')}
                  className="app-input"
                  required
                />
                <span
                  className="input-icon-right"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  <IonIcon icon={showConfirm ? eyeOffOutline : eyeOutline} />
                </span>
              </div>

              {error && (
                <div className="status-alert alert-error">
                  <IonIcon icon={alertCircleOutline} />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="status-alert alert-success">
                  <IonIcon icon={checkmarkCircleOutline} />
                  <span>{success}</span>
                </div>
              )}

              <IonButton
                type="submit"
                expand="block"
                className="app-btn-primary"
                disabled={loading}
              >
                {loading ? <IonSpinner name="crescent" /> : 'Actualizar Contraseña'}
              </IonButton>
            </form>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CambiarContrasena;
